import router from "~/plugins/VueRouter";
import { useAppTabs } from "~/stores/useAppTabs";
import { activeGridContext } from "~/utils/Grids/AssistantScreenContext";
import { useApiResources } from "~stores/ApiResources";

/** The screen context sent alongside a question. Mirrors the backend's `AssistantContextData`. */
export interface AssistantScreenContext {
	resource?: string;
	view?: string;
	recordId?: string;
	criteria?: string;
	label?: string;
}

export interface ResolvedScreenContext {
	context: AssistantScreenContext;
	/** What the palette's context chip shows — the tab title the user can recognise. */
	summary: string;
	/** Whether an on-screen grid filter is part of the context (the chip flags it). */
	hasFilter: boolean;
}

/**
 * Describes the screen the user has open, so the assistant can resolve a question that names no subject
 * of its own ("hoeveel zijn er dit jaar?", "wat is de totale waarde hiervan?") against it.
 *
 * Resolved on demand rather than tracked continuously: there is nothing to keep in sync, and the user can
 * switch tabs between two turns of the same conversation.
 *
 * The active **tab** is the source of truth, not `router.currentRoute`: this app never actually navigates
 * — `useRouter().push` only calls `addTab` — so the router's current route is not the screen on display.
 */
export function useAssistantContext() {
	const { activeTab } = useAppTabs();
	const { findByNameSafe } = useApiResources();

	function resolve(): ResolvedScreenContext | undefined {
		const tab = activeTab.value;
		if (!tab) return undefined; // dashboard — nothing is open

		// `/sales-order/update/<uuid>?_t=…` → ["sales-order", "update", "<uuid>"]. Both the generic
		// `/:resource/:action` routes and the per-view routes (`sales-order.update`) share this shape.
		const route = router.resolve(tab.fullPath);
		const [resourceSegment, viewSegment] = route.path.split("/").filter(Boolean);

		const resource = resourceSegment ? findByNameSafe(resourceSegment) : undefined;
		const recordId = (route.params.id as string | undefined) || undefined;

		// Only adopt the grid's filter when the grid really is this screen's own resource. A detail screen
		// carrying a child grid (sales-order rows on an order form) would otherwise contribute RSQL whose
		// fields do not exist on the context's resource.
		const grid = resource ? activeGridContext(resource.name) : undefined;
		const criteria = grid && grid.resourceKey === resource?.name ? grid.criteria || undefined : undefined;

		const context: AssistantScreenContext = {
			resource: resource?.name,
			view: viewSegment || undefined,
			recordId: resource ? recordId : undefined,
			criteria,
			label: tab.title || undefined,
		};

		// Nothing worth telling the assistant about.
		if (!context.resource && !context.label) return undefined;

		return { context, summary: tab.title || resource?.title || "", hasFilter: criteria != undefined };
	}

	return { resolve };
}
